// domain/services/coveragePriority.js
import { PRIMARY_COB_RANK } from '../constants/coverage';

/**
 * Coordination of benefits — the order in which a client's payers are billed.
 *
 * `cobRank` is a 1-based integer and lowest wins: the projector bills the lowest-ranked
 * coverage that is active on the service date. This service owns the one invariant that makes
 * that selection unambiguous — NO TWO LIVE COVERAGES SHARE A RANK — so every mutation (add,
 * remove, reorder) routes through here rather than nudging numbers in place.
 *
 * RANKS ARE COMPARED ACROSS THE LIFECYCLE GROUPS, NOT WITHIN ONE. Only the in-sequence group is
 * ordered here, but the set the projector compares is "the coverages active on the service date",
 * and for a past date that spans the ended group too. So the ranks an ended coverage holds are
 * RESERVED: it keeps the position it had when it was live and is never renumbered, and the
 * in-sequence group is assigned the lowest ranks it does not already hold. Compaction closes the
 * gaps a removal leaves, but never by taking a number an ended coverage is still holding.
 *
 * That is why the sequence is contiguous from 1 only when nothing has ended. Contiguity was never
 * the point — uniqueness is; the number a user sees is derived separately over the billable subset
 * (`coverageBilling.billingOrderNumbers`) and does not read `cobRank` at all. Compacting the
 * sequence to 1..n while an ended coverage sat on rank 1 is exactly the collision this prevents:
 * the two were both active on some past date, both would answer "primary", and the projector would
 * pick whichever the query returned first.
 *
 * POSITION IS INDEPENDENT OF EVERYTHING ELSE, deliberately. Every function here filters on
 * `isDeleted` and nothing else: an unverified or held coverage holds a position exactly like a
 * billable one. COB order is a fact about the plans, fixed by rule (birthday rule, court order,
 * Medicaid-last) and usually knowable at intake without contacting anyone — two parents' dates of
 * birth settle the birthday rule, and eligibility coming back does not change that answer. Do not
 * add an eligibility or on-hold filter to these functions.
 *
 * There is no separate "confirm the order" step: the arrangement IS the order, and dragging is how
 * a user sets it.
 *
 * A REORDER IS RETROACTIVE, and that is deliberate. `cobRank` is a single value, not an
 * effective-dated series, so changing it changes the answer for every service date — including
 * past activities that have not yet been claimed, which reproject under the new order. That is
 * correct for the intent dragging actually expresses: "the order was recorded wrong." It is wrong
 * for "the order changed in March", and that case has its own expression — end the old coverage
 * and add a new one with the new dates, exactly as removal distinguishes delete from end. Reality
 * changing has a date; a mistake does not.
 *
 * Items already claimed are unaffected either way: the projector refuses to modify anything at or
 * past CLAIMED, so a plan billed under the old order keeps it.
 *
 * `cobRank` is the POSITION. The number a row displays is a separate derivation over the billable
 * subset — see `coverageBilling.billingOrderNumbers`. Non-billable rows keep a position here and
 * are skipped there, so dragging one shows where it will land once it becomes billable.
 *
 * These functions are date-agnostic: they rank whatever list they are handed, and the reserved
 * ranks arrive as plain numbers rather than as coverages. Which coverages have ended is decided by
 * `coverageGroups.partitionByLifecycle` before anything gets here — this service never asks what
 * day it is.
 *
 * Ordering is over the client's whole coverage list rather than per effective period. Nothing
 * is lost: the projector filters to the coverages active on the service date *before* ranking,
 * so period-scoped precedence falls out of the effective dates for free.
 */

const isLive = (coverage) => coverage.isDeleted !== true;

/** The first rank at or above `from` that nothing is holding. */
const firstFreeRank = (from, reservedRanks) => {
  let rank = from;
  while (reservedRanks.has(rank)) {
    rank += 1;
  }
  return rank;
};

/**
 * The ranks a set of coverages is holding, to be passed to the ranking functions as off-limits.
 *
 * Takes coverages rather than being hand-rolled at the call site so that "removed coverages hold
 * nothing" is decided once, here: a tombstone is invisible to every reader, so its rank is free.
 *
 * @param {import('../model/Coverage.types').Coverage[]} coverages
 * @returns {Set<number>}
 */
export function reservedRanksOf(coverages) {
  return new Set(
    coverages
      .filter(isLive)
      .map((coverage) => coverage.cobRank)
      .filter((cobRank) => Number.isInteger(cobRank)),
  );
} // reservedRanksOf()

/**
 * Live coverages in billing order. Ties (and unranked rows) fall back to a stable-by-id order
 * so the list never reshuffles itself between renders.
 *
 * @param {import('../model/Coverage.types').Coverage[]} coverages
 * @returns {import('../model/Coverage.types').Coverage[]}
 */
export function sortByPriority(coverages) {
  return coverages
    .filter(isLive)
    .slice()
    .sort((a, b) => {
      const rankA = Number.isInteger(a.cobRank) ? a.cobRank : Number.MAX_SAFE_INTEGER;
      const rankB = Number.isInteger(b.cobRank) ? b.cobRank : Number.MAX_SAFE_INTEGER;
      if (rankA !== rankB) {
        return rankA - rankB;
      }
      return `${a.id}`.localeCompare(`${b.id}`);
    });
} // sortByPriority()

/**
 * Restamps an already-ordered list from the lowest ranks available, skipping any that are
 * reserved. Contiguous 1..n when nothing is reserved. Returns new objects; the input is not
 * mutated.
 *
 * @param {import('../model/Coverage.types').Coverage[]} orderedCoverages
 * @param {Set<number>} [reservedRanks] - held by coverages outside this list; see `reservedRanksOf`
 * @returns {import('../model/Coverage.types').Coverage[]}
 */
export function assignCobRanks(orderedCoverages, reservedRanks = new Set()) {
  let next = PRIMARY_COB_RANK;

  return orderedCoverages.map((coverage) => {
    const cobRank = firstFreeRank(next, reservedRanks);
    next = cobRank + 1;
    return { ...coverage, cobRank };
  });
} // assignCobRanks()

/**
 * The rank a newly added coverage should take — last, so adding never silently changes which
 * payer is billed first.
 *
 * Past the HIGHEST rank in the list rather than past its length: with a rank reserved somewhere in
 * the middle the sequence has a gap, and counting rows would land the new coverage on top of one
 * that is already there.
 *
 * @param {import('../model/Coverage.types').Coverage[]} coverages
 * @param {Set<number>} [reservedRanks]
 * @returns {number}
 */
export function nextCobRank(coverages, reservedRanks = new Set()) {
  const highest = coverages
    .filter(isLive)
    .map((coverage) => coverage.cobRank)
    .filter((cobRank) => Number.isInteger(cobRank))
    .reduce((max, cobRank) => Math.max(max, cobRank), PRIMARY_COB_RANK - 1);

  return firstFreeRank(highest + 1, reservedRanks);
} // nextCobRank()

/**
 * Re-sorts live coverages into the given id order and restamps ranks. Ids not present in the
 * list are ignored; live coverages missing from `orderedIds` keep their relative order at the
 * end, so a stale drag payload degrades instead of dropping rows.
 *
 * @param {import('../model/Coverage.types').Coverage[]} coverages
 * @param {string[]} orderedIds
 * @param {Set<number>} [reservedRanks]
 * @returns {import('../model/Coverage.types').Coverage[]} live coverages, reranked
 */
export function applyOrder(coverages, orderedIds, reservedRanks = new Set()) {
  const live = sortByPriority(coverages);
  const byId = new Map(live.map((coverage) => [coverage.id, coverage]));

  const ordered = orderedIds
    .map((id) => byId.get(id))
    .filter((coverage) => coverage !== undefined);

  const orderedSet = new Set(ordered.map((coverage) => coverage.id));
  const remainder = live.filter((coverage) => !orderedSet.has(coverage.id));

  return assignCobRanks([...ordered, ...remainder], reservedRanks);
} // applyOrder()

/**
 * Moves one coverage up (-1) or down (+1) in billing order. This is the keyboard-accessible
 * companion to drag reordering, which is pointer-only.
 *
 * @param {import('../model/Coverage.types').Coverage[]} coverages
 * @param {string} id
 * @param {number} offset
 * @param {Set<number>} [reservedRanks]
 * @returns {import('../model/Coverage.types').Coverage[]} live coverages, reranked
 */
export function moveByOffset(coverages, id, offset, reservedRanks = new Set()) {
  const live = sortByPriority(coverages);
  const from = live.findIndex((coverage) => coverage.id === id);
  const to = from + offset;

  if (from === -1 || to < 0 || to >= live.length) {
    return assignCobRanks(live, reservedRanks);
  }

  const reordered = live.slice();
  const [moved] = reordered.splice(from, 1);
  reordered.splice(to, 0, moved);

  return assignCobRanks(reordered, reservedRanks);
} // moveByOffset()

/**
 * Closes the rank gaps a removal leaves behind, without taking a reserved rank.
 *
 * @param {import('../model/Coverage.types').Coverage[]} coverages
 * @param {Set<number>} [reservedRanks]
 * @returns {import('../model/Coverage.types').Coverage[]} live coverages, reranked
 */
export function compactRanks(coverages, reservedRanks = new Set()) {
  return assignCobRanks(sortByPriority(coverages), reservedRanks);
} // compactRanks()
